import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Upload } from 'antd';
import ImgCrop from 'antd-img-crop';
import {
  ArrowRight,
  Bank,
  CameraOutline,
  Information,
  Language,
  Logout,
  MagicPen,
  Preferences,
  Settings,
  WarningCircleLinear,
} from '../icons';
import { useFetchUser } from '../../hooks/useFetchUser';
import ConfirmModal from '../confirmationmodal/confirmationModal';
import { useAuthenticationProvider } from '../../authentication/authProvider';
import { useFetchUserParameters } from '../../hooks/useFetchUserParameters';
import { useFetchTransactionTypes } from '../../hooks/useFetchTransactionTypes';
import ProfileModal from './profileModal';
import AppearanceSettings from './appearanceSettings';
import OrganisationList from './organisationList';
import TransactionSetting from './transactionSetting';
import {
  ActivePopover,
  LOCAL_STORAGE_NAMES,
  ROUTES,
  ThemeMode,
  UserType,
} from '../../constants/constants';
import { useFetchOrganisationList } from '../../hooks/useFetchOrganisationList';
import {
  getProfilePictureValidationError,
  getThemeTranslation,
} from '../../constants/utilityFunctions';
import { useUser } from '../../context/UserContext';
import { useTransactionParameters } from '../../context/TransactionParameterContext';
import { Button, Heading, Paragraph, SettingButton, Spin, Text } from '../Ant';
import {
  AvatarErrorMessage,
  AvatarWrapper,
  SettingContent,
  UserDetails,
  UserWrapper,
} from '../../assets/styles/components/settingsPopover.styled';
import { FormCard } from '../../assets/styles/components/formCard.styled';
import { useUploadProfilePicture } from '../../hooks/useUploadProfilePicture';
import { useDeleteProfilePicture } from '../../hooks/useDeleteProfilePicture';
import { usePage } from '../../context/PageContext';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsEvent, FeatureButtons } from '../../interface/analytics';
import { stopAllRunningTimersForCurrentOrg } from '../../hooks/useTimerActions';
import UserAvatarCircle from '../userAvatarCircle/userAvatarCircle';

interface SettingsItem {
  icon: React.ReactNode;
  text: string;
  subText?: string;
  value: string;
  route?: string;
}

const RESIZABLE_POPOVERS = new Set<string>([
  ActivePopover.ORGANISATION,
  ActivePopover.TRANSACTION_PARAMETERS,
]);

const readCurrentOrganisation = (): IOrganisation | null => {
  const storedOrganisation = localStorage.getItem(LOCAL_STORAGE_NAMES.currentOrganisation);
  return storedOrganisation ? JSON.parse(storedOrganisation) : null;
};

const Profile = () => {
  const { t } = useTranslation();
  const { updateUser, updateUserDetails, user: currentUser } = useUser();
  const { logout } = useAuthenticationProvider();
  const { setTitle } = usePage();
  const { updateParameters } = useTransactionParameters();
  const { push, goBack } = useNavigationStack();
  const { trackAction } = useAnalytics();

  const {
    refetch: refetchUser,
    data: userDetails,
    isLoading: isLoadingUser,
  } = useFetchUser({
    relations: ['businessUnit', 'userRole', 'userGroups'],
  });
  const { data: organisationList = [] } = useFetchOrganisationList();
  const { data: parametersDetails } = useFetchUserParameters({ parametersToGet: 'transaction' });
  const defaultTransactionTypeId =
    parametersDetails?.parameters?.transactionParameters?.defaultTransactionTypeId ?? null;
  const { data: transactionTypesDetails } = useFetchTransactionTypes({
    defaultTransactionTypeId,
  });
  const uploadProfilePictureMutation = useUploadProfilePicture();
  const deleteProfilePictureMutation = useDeleteProfilePicture();
  const [imageUrl, setImageUrl] = useState(userDetails?.imageUrl || null);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const [isLogoutModalVisible, setIsLogoutModalVisible] = useState<boolean>(false);
  const [isUploadModal, setIsUploadModal] = useState<boolean>(false);
  const [activePopover, setActivePopover] = useState<{ key: string | null; text: string | null }>({
    key: null,
    text: null,
  });

  const [selectOrganisation, setSelectOrganisation] = useState<IOrganisation | null>(null);

  const closeActivePopover = () => {
    setActivePopover({ key: null, text: null });
    setSelectOrganisation(readCurrentOrganisation());
  };

  const handleBack = () => {
    goBack();
  };
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        title={t('common:bottomNavigation.profile')}
        showMenu
        showSettings
        showHomeWithBackButton={false}
        handleBack={handleBack}
      />,
    );
  }, []);

  const settingsItems: SettingsItem[] = [
    {
      icon: <MagicPen />,
      text: t('common:profile.appearance'),
      subText: getThemeTranslation(
        localStorage.getItem(LOCAL_STORAGE_NAMES.theme) ?? ThemeMode.light,
        t,
      ),
      value: ActivePopover.APPEARANCE,
    },
    {
      icon: <Bank />,
      text: t('common:organization'),
      subText: readCurrentOrganisation()?.organizationName ?? '',
      value: ActivePopover.ORGANISATION,
    },
    {
      icon: <Language />,
      text: t('common:profile.languageAndRegionalFormat'),
      subText: t('common:profile.languageAndRegionalFormatDescription'),
      value: ActivePopover.LANGUAGE,
      route: ROUTES.LANGUAGE_AND_REGION,
    },
    {
      icon: <Settings />,
      text: t('common:profile.applicationParameters'),
      subText: t('common:profile.applicationParametersDescription'),
      value: ActivePopover.TRANSACTION_PARAMETERS,
    },
    {
      icon: <Preferences />,
      text: t('common:profile.preferences'),
      subText: t('common:profile.preferencesDescription'),
      value: ActivePopover.PREFERENCES,
      route: ROUTES.PREFERENCES,
    },
    {
      icon: <Information />,
      text: t('common:profile.about'),
      value: ActivePopover.ABOUT,
      route: ROUTES.PROFILE_ABOUT,
    },
  ];

  const getUserTypeTranslation = (userType?: string) => {
    switch (userType) {
      case UserType.ADMIN:
        return t('common:profile.userType.admin');
      case UserType.NORMAL:
        return t('common:profile.userType.normal');
      case UserType.RESTRICTED:
        return t('common:profile.userType.restricted');
      default:
        return userType;
    }
  };

  const getPopoverContent = () => {
    switch (activePopover?.key) {
      case ActivePopover.APPEARANCE:
        return (
          <AppearanceSettings
            onSubmit={() => {
              setActivePopover({ key: null, text: null });
            }}
          />
        );
      case ActivePopover.ORGANISATION:
        return (
          <OrganisationList
            selectOrganisation={selectOrganisation ?? undefined}
            organisationList={organisationList ?? []}
            setSelectOrganisation={(organisation) => {
              setSelectOrganisation(organisation);
            }}
            handleSelectOrganisation={handleSelectOrganisation}
          />
        );
      case ActivePopover.TRANSACTION_PARAMETERS:
        return (
          <TransactionSetting
            transactionParameters={parametersDetails?.parameters?.transactionParameters}
            transactionTypesDetails={transactionTypesDetails}
          />
        );
      default:
        return <></>;
    }
  };

  const handleSelectOrganisation = (organisation: IOrganisation) => {
    // Stop all running timers for the current organization before switching
    stopAllRunningTimersForCurrentOrg(currentUser?.id ?? '');
    localStorage.setItem(LOCAL_STORAGE_NAMES.currentOrganisation, JSON.stringify(organisation));
    refetchUser().then((data) => {
      const user = data?.data;
      if (!user) {
        return;
      }
      updateUser({
        id: user.id,
        fullName: user.fullName,
        email: user.email,
        userType: user.userType,
        status: user.status,
        userRole: {
          hidePricingInformation: user.userRole?.hidePricingInformation ?? false,
        },
      });
      setTimeout(() => {
        globalThis.location.reload();
      }, 1000);
    });
  };
  const applyProfilePicture = (url: string | null) => {
    setImageUrl(url);
    setAvatarError(null);
  };

  const showAvatarError = (message: string) => {
    setIsUploadModal(false);
    setAvatarError(message);
  };

  const isSelectedPhotoValid = (file: File) => {
    const validationError = getProfilePictureValidationError(file, t);
    if (validationError) {
      showAvatarError(validationError);
      return false;
    }
    setAvatarError(null);
    return true;
  };

  const handleRemoveProfilePhoto = () => {
    setIsUploadModal(false);
    deleteProfilePictureMutation.mutate(
      {},
      {
        onSuccess() {
          setImageUrl(null);
        },
      },
    );
  };
  const handleImageChange = (file: File) => {
    setIsUploadModal(false);
    const formData = new FormData();
    formData.append('file', file);
    uploadProfilePictureMutation.mutate(
      { data: formData },
      {
        onSuccess: (data) => {
          applyProfilePicture(data.url);
        },
        onError: () => {
          showAvatarError(t('common:profile.uploadPhotoFailed'));
        },
      },
    );
  };

  useEffect(() => {
    setSelectOrganisation(readCurrentOrganisation());

    refetchUser();
  }, []);

  const handleLogout = () => {
    trackAction(AnalyticsEvent.BUTTON_CLICK, FeatureButtons.PROFILE_LOGOUT);
    logout();
    setIsLogoutModalVisible(false);
  };
  const actionMap: Record<string, { event: AnalyticsEvent; feature: FeatureButtons }> = {
    [ActivePopover.ORGANISATION]: {
      event: AnalyticsEvent.BUTTON_CLICK,
      feature: FeatureButtons.PROFILE_CHANGE_ORGANISATION,
    },
    [ActivePopover.APPEARANCE]: {
      event: AnalyticsEvent.BUTTON_CLICK,
      feature: FeatureButtons.PROFILE_APPEARANCE,
    },
    [ActivePopover.LANGUAGE]: {
      event: AnalyticsEvent.BUTTON_CLICK,
      feature: FeatureButtons.PROFILE_LANGUAGE,
    },
    [ActivePopover.TRANSACTION_PARAMETERS]: {
      event: AnalyticsEvent.BUTTON_CLICK,
      feature: FeatureButtons.PROFILE_TRANSACTION_SETTINGS,
    },
    [ActivePopover.PREFERENCES]: {
      event: AnalyticsEvent.BUTTON_CLICK,
      feature: FeatureButtons.PROFILE_PREFERENCES,
    },
    [ActivePopover.ABOUT]: {
      event: AnalyticsEvent.BUTTON_CLICK,
      feature: FeatureButtons.PROFILE_ABOUT,
    },
  };

  const onSettingsMenuClicked = (item: SettingsItem) => {
    const action = actionMap[item.value];
    if (action) {
      trackAction(action.event, action.feature);
    }
    if (item.route) {
      push(item.route);
      return;
    }
    setActivePopover({ key: item.value, text: item.text });
  };
  useEffect(() => {
    if (parametersDetails) {
      updateParameters(parametersDetails?.parameters?.transactionParameters);
    }
  }, [parametersDetails]);

  useEffect(() => {
    if (userDetails) {
      updateUserDetails(userDetails);
      setImageUrl(userDetails?.imageUrl || null);
    }
  }, [userDetails]);

  return (
    <>
      {isLoadingUser && <Spin fullscreen />}

      <SettingContent
        vertical
        gap={15}
        className="flex-grow overflow-y-auto scroll-page-gutter pb-gutter"
      >
        <Flex gap={5} vertical justify="flex-start">
          <Flex align="center" gap={10} className="relative">
            <AvatarWrapper justify="center" align="center">
              <Button
                data-testid="user-photo-upload-button"
                aria-label={t('common:profile.choosePhoto')}
                aria-haspopup="dialog"
                className="photo-upload-wrapper"
                onClick={() => setIsUploadModal(true)}
              >
                <CameraOutline />
              </Button>
              <UserAvatarCircle
                imageUrl={imageUrl}
                fullName={userDetails?.fullName}
                isProfileSize
              />
            </AvatarWrapper>
            <UserDetails>
              <UserWrapper data-testid="user-type">
                {getUserTypeTranslation(userDetails?.userType)}
              </UserWrapper>
              <Text
                data-testid="user-business-unit"
                $color="subtitle"
                $fontsize="12px"
                $lineheight="18px"
              >
                {userDetails?.businessUnit?.name ?? ''}
              </Text>
              <Heading
                data-testid="user-full-name"
                level={3}
                $fontsize="14px"
                $weight={700}
                $lineheight="18px"
                $color="title"
              >
                {userDetails?.fullName ?? ''}
              </Heading>
              <Paragraph
                $fontsize="12px"
                $color="description"
                $lineheight="16px"
                className="word-break-all"
                data-testid="user-email"
              >
                {userDetails?.email ?? ''}
              </Paragraph>
            </UserDetails>
          </Flex>
          {avatarError && (
            <AvatarErrorMessage gap={4} align="center" data-testid="avatar-error-message">
              <WarningCircleLinear />
              <span>{avatarError}</span>
            </AvatarErrorMessage>
          )}
        </Flex>

        <FormCard className="h-full">
          <Flex vertical gap={10}>
            {settingsItems.map((item) => (
              <SettingButton
                data-testid={`settings-item-${item.value}`}
                key={item.value}
                onClick={() => {
                  onSettingsMenuClicked(item);
                }}
                block
              >
                <span className="start-icon">{item.icon}</span>
                <div className="w-full d-flex items-center">
                  <Flex vertical className="w-full">
                    <Text
                      $lineheight="20px"
                      $weight={600}
                      data-testid={`settings-item-${item.value}`}
                      $color="title"
                      $fontsize="12px"
                      className="ta-left text-wrap"
                    >
                      {item.text}
                    </Text>
                    {item.subText && (
                      <Text
                        $fontsize="12px"
                        $lineheight="20px"
                        data-testid={`settings-item-${item.value}-subtext`}
                        className="ta-left text-wrap"
                        $color="subtitle"
                      >
                        {item.subText}
                      </Text>
                    )}
                  </Flex>
                  <span className="end-icon">
                    <ArrowRight />
                  </span>
                </div>
              </SettingButton>
            ))}
            <SettingButton
              data-testid="settings-logout-id"
              onClick={() => setIsLogoutModalVisible(true)}
              block
            >
              <span className="start-icon">
                <Logout className="logout" />
              </span>
              <div className="w-full d-flex items-center">
                <div className="d-flex fd-col w-full">
                  <Text
                    $lineheight="20px"
                    $weight={600}
                    $color="title"
                    $fontsize="12px"
                    className="ta-left"
                  >
                    {t('common:logout')}
                  </Text>
                </div>
                <span className="end-icon">
                  <ArrowRight />
                </span>
              </div>
            </SettingButton>
          </Flex>
        </FormCard>
      </SettingContent>

      <ConfirmModal
        visible={isLogoutModalVisible}
        onClose={() => setIsLogoutModalVisible(false)}
        icon={<Logout />}
        title={t('common:logOutQuestion')}
        subtitle={t('common:logOutMessage')}
        cancelText={t('common:cancel')}
        confirmText={t('common:logOutBtnText')}
        onConfirm={handleLogout}
      />
      {activePopover.key && (
        <ProfileModal
          isOpen={!!activePopover.key}
          onClose={closeActivePopover}
          title={settingsItems.find((item) => item.value === activePopover.key)?.text ?? ''}
          resizable={RESIZABLE_POPOVERS.has(activePopover.key)}
        >
          {getPopoverContent()}
        </ProfileModal>
      )}
      <ProfileModal
        isOpen={isUploadModal}
        onClose={() => setIsUploadModal(false)}
        title={t('common:profile.chooseOption')}
      >
        <Flex gap={10} vertical>
          <ImgCrop
            rotationSlider
            aspect={1}
            modalTitle={t('common:profile.cropPhoto')}
            modalOk={t('common:upload')}
            modalCancel={t('common:cancel')}
            beforeCrop={(file) => {
              return isSelectedPhotoValid(file);
            }}
            showReset
            modalWidth={300}
          >
            <Upload
              className="photo-upload"
              showUploadList={false}
              accept="image/*"
              customRequest={({ file }) => {
                if (file instanceof File && isSelectedPhotoValid(file)) {
                  handleImageChange(file);
                }
              }}
            >
              <Button data-testid="user-photo-upload-button" size="large" type="primary" block>
                {t('common:profile.chooseFromGallery')}
              </Button>
            </Upload>
          </ImgCrop>
          <Button
            data-testid="user-photo-remove-button"
            size="large"
            disabled={!imageUrl}
            danger
            block
            onClick={handleRemoveProfilePhoto}
          >
            {t('common:profile.removePhoto')}
          </Button>
        </Flex>
      </ProfileModal>
    </>
  );
};

export default Profile;
